import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, HandCoins, Inbox, Settings, Smartphone, UserPlus, PiggyBank, Clock, Wallet, LogOut, History } from "lucide-react";
import { Brand, UserPhoto } from "@/components/Brand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { StatPill, PageHero } from "@/components/Colorful";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { rp, rank, ROLE_LABEL } from "@/lib/format";

const NAV = [
  { to: "/admin", label: "Dasbor", icon: LayoutDashboard, end: true, id: "dashboard", color: "text-blue-600 bg-blue-50" },
  { to: "/admin/anggota", label: "Anggota", icon: Users, id: "members", color: "text-emerald-600 bg-emerald-50" },
  { to: "/admin/pendaftaran", label: "Pendaftaran", icon: UserPlus, id: "registrations", color: "text-violet-600 bg-violet-50" },
  { to: "/admin/pinjaman", label: "Pinjaman", icon: HandCoins, id: "loans", color: "text-orange-600 bg-orange-50" },
  { to: "/admin/permintaan", label: "Pembayaran & Transfer", icon: Inbox, id: "requests", color: "text-rose-600 bg-rose-50" },
  { to: "/admin/log", label: "Log Perubahan", icon: History, id: "logs", color: "text-amber-600 bg-amber-50", minRank: 2 },
  { to: "/admin/pengaturan", label: "Pengaturan", icon: Settings, id: "settings", color: "text-teal-600 bg-teal-50", minRank: 2 },
];

const TopPills = ({ s, go }) => (
  <div className="flex gap-2.5 overflow-x-auto no-scrollbar px-4 md:px-6 py-3 bg-white border-b border-slate-200" data-testid="admin-stat-pills">
    <StatPill tone="blue" icon={Users} label="Anggota" value={s?.members ?? "-"} onClick={() => go("/admin/anggota")} testid="pill-members" />
    <StatPill tone="green" icon={PiggyBank} label="Total Simpanan" value={rp(s?.total_savings)} onClick={() => go("/admin/anggota")} testid="pill-savings" />
    <StatPill tone="orange" icon={Wallet} label="Piutang" value={rp(s?.outstanding_loans)} onClick={() => go("/admin/pinjaman")} testid="pill-outstanding" />
    <StatPill tone="purple" icon={UserPlus} label="Pendaftar" value={s?.pending_registrations ?? 0} onClick={() => go("/admin/pendaftaran")} testid="pill-registrations" />
    <StatPill tone="red" icon={Clock} label="Pinjaman Menunggu" value={s?.pending_loans ?? 0} onClick={() => go("/admin/pinjaman")} testid="pill-pending-loans" />
    <StatPill tone="teal" icon={Inbox} label="Verifikasi" value={s?.pending_requests ?? 0} onClick={() => go("/admin/permintaan")} testid="pill-pending-requests" />
  </div>
);

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, [location.pathname]);
  useEffect(() => {
    const refresh = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    window.addEventListener("cu-admin-changed", refresh);
    return () => window.removeEventListener("cu-admin-changed", refresh);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-rose-50/40">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 md:px-6 py-2.5 border-b border-slate-100">
          <Brand />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-semibold text-slate-900">{user.name}</div>
              <div className="text-[11px] font-semibold text-orange-600" data-testid="admin-role-label">{ROLE_LABEL[user.role]}</div>
            </div>
            <UserPhoto user={user} className="h-9 w-9 rounded-full text-xs" />
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout" className="p-2 rounded-full hover:bg-red-50 text-slate-500 hover:text-red-600" aria-label="Keluar"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        <TopPills s={stats} go={navigate} />
      </header>
      <div className="flex flex-col md:flex-row">
        <aside className="md:w-64 md:min-h-[calc(100vh-120px)] bg-white/80 border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col">
          <div className="hidden md:block px-5 pt-5 pb-2 text-[11px] font-bold tracking-[0.2em] text-slate-400">MENU PENGURUS</div>
          <nav className="flex md:flex-col gap-1 p-2 md:p-3 overflow-x-auto no-scrollbar flex-1">
            {NAV.filter((n) => rank(user) >= (n.minRank || 1)).map(({ to, label, icon: Icon, end, id, color }) => (
              <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${id}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-gradient-to-r from-orange-100 to-amber-50 text-orange-700 ring-1 ring-orange-200" : "text-slate-700 hover:bg-slate-100"}`}>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="h-4 w-4" /></span>{label}
              </NavLink>
            ))}
          </nav>
          <button onClick={() => navigate("/app")} data-testid="admin-back-to-app" className="m-2 md:m-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-blue-800 bg-blue-50 hover:bg-blue-100 whitespace-nowrap">
            <Smartphone className="h-4 w-4" /> Aplikasi Anggota
          </button>
        </aside>
        <main className="flex-1 p-4 md:p-8 min-w-0"><Outlet /></main>
      </div>
    </div>
  );
}

export const AdminTitle = ({ title, sub, icon = LayoutDashboard, children }) => (
  <PageHero icon={icon} title={title} sub={sub}>{children}</PageHero>
);
